import { inject, Injectable, signal } from '@angular/core';
import { WA_WINDOW } from '@ng-web-apis/common';

// Fetches are cancelled the moment a redirect starts, so a manual mark only has to hold briefly. If the page is
// still alive after this, the redirect was cancelled (a beforeunload "Stay", the Stop button) and later fetch
// failures are real.
const MANUAL_MARK_TTL_MS = 5000;

// Whether the current document is about to be replaced, so aborted fetches can be told apart from real failures.
@Injectable({ providedIn: 'root' })
export class PageLeavingService {
  private readonly window = inject<Window>(WA_WINDOW);
  private readonly leaving = signal(false);
  private manualMarkTimer: number | undefined;

  public readonly isLeaving = this.leaving.asReadonly();

  public constructor() {
    // Deliberately no beforeunload listener: Firefox keeps a page that has one out of the bfcache,
    // which costs every back/forward a full reload.
    this.window.addEventListener('pagehide', () => {
      this.setLeaving(true);
    });
    // A bfcache restore reuses the document, so it has to stop counting as leaving.
    this.window.addEventListener('pageshow', () => {
      this.setLeaving(false);
    });

    // SAFETY: lib.dom types `navigation` as always present, but Safari before 26.2 and the Jest window stub lack it.
    const navigation = (this.window as { navigation?: Navigation }).navigation;

    if (!navigation) {
      return;
    }

    // WebKit cancels in-flight fetches when a navigation starts, before pagehide fires.
    navigation.addEventListener('navigate', (event) => {
      // Router navigations use the History API, so they arrive as same-document events.
      if (event.destination.sameDocument || typeof event.downloadRequest === 'string') {
        return;
      }

      this.setLeaving(true);
      event.signal.addEventListener(
        'abort',
        () => {
          this.setLeaving(false);
        },
        { once: true },
      );
    });
    navigation.addEventListener('navigateerror', () => {
      this.setLeaving(false);
    });
  }

  // For HardNavigationService, ahead of a navigation it starts itself. Browsers without the Navigation API never
  // announce the navigation before pagehide, so the mark expires on its own in case the redirect is cancelled.
  public markLeaving(): void {
    this.setLeaving(true);
    this.manualMarkTimer = this.window.setTimeout(() => {
      this.manualMarkTimer = undefined;
      this.leaving.set(false);
    }, MANUAL_MARK_TTL_MS);
  }

  // Any browser-announced change takes over from a pending manual expiry, so the timer cannot clear a flag it did not set.
  private setLeaving(value: boolean): void {
    if (this.manualMarkTimer !== undefined) {
      this.window.clearTimeout(this.manualMarkTimer);
      this.manualMarkTimer = undefined;
    }

    this.leaving.set(value);
  }
}
